import React, { FC, ChangeEvent, useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import _debounce from 'lodash/debounce';
import { useTranslation } from '@blocs.i18n';
import SearchInput from '@minecraft.search-input';
import Button from '@minecraft.button';
import { helpers } from '@minecraft.utils';
import { CheckboxListWithSearchProps } from '../shared/types';
import CheckboxList from '../CheckboxList';

const { searchOptions } = helpers;

const CheckboxListHead = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.625rem;
`;

const SeeMoreButton = styled(Button)`
  align-self: flex-end;
  color: ${(props): string => props.theme.colors.buttons.primary.default};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0;
  height: auto;
  min-width: unset;
  margin-top: 0.375rem;

  &,
  &:hover,
  &:focus,
  &:active,
  &:not(:disabled):not(.disabled):active {
    color: ${(props): string => props.theme.colors.buttons.primary.hover};
    text-decoration: none;
  }
`;

const CheckboxListWithSearch: FC<Partial<CheckboxListWithSearchProps>> = ({
  withFormik,
  withAutocomplete,
  withSelectAll,
  isLoading,
  initialOffset,
  initialLimit,
  name,
  optionsTotalCount,
  options,
  initialSelectedOptions,
  initialDisabledOptions,
  onOptionsChange,
  onOptionsFetch,
  onOptionsFetchMore,
  appliedQueryInput,
  refetchProfileFilterData,
  renderFieldLabel,
}) => {
  const { t } = useTranslation();
  const [searchInputValue, setSearchInputValue] = useState('');

  const visibleOptions = useMemo(() => {
    if (!withAutocomplete) {
      return searchOptions(searchInputValue, options);
    }

    return options;
  }, [withAutocomplete, searchInputValue, options]);

  const handleSearchInputValueChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchInputValue(event.currentTarget.value);
  };

  const handleOptionsFetch = useCallback((): void => {
    onOptionsFetch && onOptionsFetch(searchInputValue, initialOffset, initialLimit);
  }, [searchInputValue, onOptionsFetch]);

  const handleOptionsFetchMore = useCallback((): void => {
    const hasNextOffset = options.length < optionsTotalCount;
    const nextOffset = Math.floor(options.length / initialLimit) + 1;

    if (!hasNextOffset) return;

    onOptionsFetchMore && onOptionsFetchMore(nextOffset, initialLimit);
  }, [initialLimit, optionsTotalCount, options, onOptionsFetchMore]);

  const debouncedSearch = useCallback(_debounce(handleOptionsFetch, 1000, { maxWait: 1000 }), [handleOptionsFetch]);

  useEffect(() => {
    handleOptionsFetch();
  }, []);

  useEffect(() => {
    if (withAutocomplete) {
      debouncedSearch();
    }

    // Cancel the debounce on useEffect cleanup.
    return (): void => {
      debouncedSearch.cancel();
    };
  }, [withAutocomplete, searchInputValue]);

  return (
    <>
      <CheckboxListHead>
        <SearchInput
          name="dropdown-search-input"
          placeholder={t('common:button.search')}
          value={searchInputValue}
          onChange={handleSearchInputValueChange}
        />
      </CheckboxListHead>
      <CheckboxList
        withFormik={withFormik}
        withSelectAll={withSelectAll}
        isLoading={isLoading}
        name={name}
        options={visibleOptions}
        initialSelectedOptions={initialSelectedOptions}
        initialDisabledOptions={initialDisabledOptions}
        onOptionsChange={onOptionsChange}
        appliedQueryInput={appliedQueryInput}
        refetchProfileFilterData={refetchProfileFilterData}
        renderFieldLabel={renderFieldLabel}
      />
      {withAutocomplete && (
        <div>
          <SeeMoreButton
            color="link"
            disabled={isLoading || optionsTotalCount <= options?.length}
            onClick={handleOptionsFetchMore}
          >
            {t('common:button.seeMore')}
          </SeeMoreButton>
        </div>
      )}
    </>
  );
};

export default CheckboxListWithSearch;
